import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { Upload, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STATUS_STEPS = [
  { key: "queued", label: "Uploading file…" },
  { key: "extracting", label: "Reading the report…" },
  { key: "narrating", label: "Writing clinical & patient summaries…" },
  { key: "ready", label: "Done" },
];

export default function UploadReport() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(params.get("patient") || "");
  const [autoDetect, setAutoDetect] = useState(false);
  const [modality, setModality] = useState("X-Ray");
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("queued");
  const fileInput = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/patients");
      setPatients(data);
      if (!patientId && data.length) setPatientId(data[0].patient_id);
    })();
    // Intentionally runs once on mount — pre-fills the dropdown with the first
    // patient only when none is preselected via the route query string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup-only effect: tears down any active polling interval on unmount.
  // `pollRef` is a useRef object (always stable) — not a real dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  };

  const pollReport = (reportId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/reports/${reportId}`);
        setStatus(data.status);
        if (data.status === "ready") {
          clearInterval(pollRef.current);
          toast.success("Report generated");
          navigate(`/reports/${reportId}`);
        } else if (data.status === "failed") {
          clearInterval(pollRef.current);
          toast.error(data.error || "Report generation failed");
          setSubmitting(false);
        }
      } catch (err) {
        // Transient network blips during polling shouldn't abort the upload —
        // the next interval tick will retry. Log only at debug level.
        if (process.env.NODE_ENV === "development") console.debug("poll retry:", err);
      }
    }, 2500);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pick a file first"); return; }
    if (!autoDetect && !patientId) { toast.error("Pick a patient or enable auto-detect"); return; }

    setSubmitting(true);
    setStatus("queued");
    try {
      const fd = new FormData();
      fd.append("modality", modality);
      fd.append("file", file);
      let endpoint = "/reports/upload";
      if (autoDetect) {
        endpoint = "/reports/quick-upload";
      } else {
        fd.append("patient_id", patientId);
      }
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      pollReport(data.report_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setSubmitting(false);
    }
  };

  if (submitting) {
    const currentIdx = STATUS_STEPS.findIndex((s) => s.key === status);
    return (
      <Layout>
        <div className="max-w-xl mx-auto py-16">
          <div className="bg-white border rounded-lg p-10 text-center" style={{ borderColor: "var(--color-border)" }}>
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-5" style={{ background: "var(--color-primary)" }}>
              <Sparkles className="w-7 h-7 text-white animate-pulse" />
            </div>
            <h2 className="font-display text-2xl font-semibold">Generating your report</h2>
            <p className="text-sm mt-2" style={{ color: "var(--color-text-secondary)" }}>
              Processing runs in the background — you can leave this tab open.
            </p>
            <div className="mt-8 space-y-3 text-left">
              {STATUS_STEPS.slice(0, 3).map((s, i) => {
                const done = currentIdx > i || status === "ready";
                const active = currentIdx === i && status !== "ready";
                return (
                  <div key={s.key} className="flex items-center gap-3 text-sm">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${done || active ? "" : "bg-[#E5E1DA]"}`} style={done || active ? { background: "var(--color-primary)" } : {}}>
                      {done ? (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>
                      ) : active ? <div className="spinner" style={{ width: 10, height: 10, borderWidth: 2 }} /> : null}
                    </div>
                    <span className={done || active ? "" : "text-[#8E9A94]"} style={done || active ? { color: "var(--color-text-primary)" } : {}}>{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>New report</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold mt-2">Upload an imaging report</h1>
        <p className="text-sm mt-2 max-w-prose" style={{ color: "var(--color-text-secondary)" }}>
          PDF, JPG or PNG up to ~10 MB. We'll extract the text and rewrite it for both clinicians and patients.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6">
          <label className="flex items-start gap-3 p-4 border rounded-md cursor-pointer hover:bg-white transition-colors" style={{ borderColor: autoDetect ? "var(--color-primary)" : "var(--color-border)", background: autoDetect ? "rgba(42,71,54,0.04)" : "transparent" }}>
            <input
              data-testid="upload-auto-detect-toggle"
              type="checkbox"
              checked={autoDetect}
              onChange={(e) => setAutoDetect(e.target.checked)}
              className="mt-1 accent-[#2A4736]"
            />
            <div>
              <div className="font-medium text-sm">Auto-detect patient from the report</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                Skip patient selection — the AI will read the report and create a new patient record for you.
              </div>
            </div>
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Patient</label>
              <select
                data-testid="upload-patient-select"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                disabled={autoDetect}
                className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: "var(--color-border)" }}
              >
                <option value="">{autoDetect ? "Auto-detect enabled" : "Choose a patient…"}</option>
                {patients.map((p) => (
                  <option key={p.patient_id} value={p.patient_id}>{p.name}</option>
                ))}
              </select>
              {patients.length === 0 && !autoDetect && (
                <p className="text-xs mt-2" style={{ color: "var(--color-warning)" }}>
                  Add a patient first, or enable auto-detect above.
                </p>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Modality</label>
              <select
                data-testid="upload-modality-select"
                value={modality}
                onChange={(e) => setModality(e.target.value)}
                className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]"
                style={{ borderColor: "var(--color-border)" }}
              >
                <option>X-Ray</option>
                <option>CT Scan</option>
                <option>MRI</option>
                <option>Ultrasound</option>
                <option>Mammography</option>
                <option>PET Scan</option>
                <option>Other Imaging</option>
              </select>
            </div>
          </div>

          <div
            data-testid="upload-dropzone"
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-colors ${dragActive ? "" : ""}`}
            style={{
              borderColor: dragActive ? "var(--color-primary)" : "var(--color-secondary)",
              background: dragActive ? "rgba(42,71,54,0.05)" : "var(--bg-canvas)",
            }}
          >
            <input
              ref={fileInput}
              data-testid="upload-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: "var(--color-primary)" }}>
              <Upload className="w-5 h-5 text-white" />
            </div>
            {file ? (
              <>
                <div className="font-medium flex items-center gap-2"><FileText className="w-4 h-4" />{file.name}</div>
                <div className="text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>{(file.size / 1024).toFixed(0)} KB · click to replace</div>
              </>
            ) : (
              <>
                <div className="font-medium">Drop your report here</div>
                <div className="text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>or click to browse · PDF, JPG, PNG</div>
              </>
            )}
          </div>

          <button
            data-testid="upload-submit-button"
            type="submit"
            disabled={!file || (!autoDetect && !patientId)}
            className="w-full py-3 rounded-md text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ background: "var(--color-primary)" }}
          >
            <Sparkles className="w-4 h-4" /> Generate report
          </button>
        </form>
      </div>
    </Layout>
  );
}
